import {
  useCreateDemoShareMutation,
  useGetCurrentUserQuery,
  useGetDemoQuery,
  useGetIntegrationsQuery,
  useGetOrganizationQuery,
} from '@carboncopyco/data';
import type {
  DemoShare,
  IDemo,
  IntegrationVendor,
  ShareLink,
} from '@carboncopyco/types';
import { notify } from '@carboncopyco/ui';
import {
  AnalyticsTrackEventName,
  removeUndefinedValues,
  useAnalytics,
} from '@carboncopyco/utils';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useIntegration } from '../../Integrations';
import { useLinksPermissions } from '../../utils/hooks/useLinksPermissions';
import { CopyShareLinkModal } from '../CopyShareLinkModal';
import {
  ShareDemoModal,
  getInitialShareDemoModalValues,
} from '../ShareDemoModal';
import type { ShareDemoModalState } from '../ShareDemoModal';

interface ShareDemoFlowProps {
  demoId: IDemo['id'];
  goToShares: () => void;
  workspaceId: string;
  onCreateSuccess?: (shareLink: DemoShare) => void;
}

export const ShareDemoFlow = NiceModal.create(
  ({
    demoId,
    workspaceId,
    goToShares,
    onCreateSuccess,
  }: ShareDemoFlowProps) => {
    const { organizationId } = useParams();
    const { data: organization } = useGetOrganizationQuery(
      {
        id: organizationId,
      },
      { skip: !organizationId }
    );
    const modal = useModal();
    const flags = useFlags();
    const { data: demo } = useGetDemoQuery({
      organizationId,
      workspaceId,
      id: String(demoId),
    });
    const { getIsDemoShareable } = useLinksPermissions();
    const { data: userResponse } = useGetCurrentUserQuery();
    const { user } = userResponse || {};

    const { data: integrations } = useGetIntegrationsQuery({
      organizationId: Number(organizationId),
    });
    const activeCRMIntegration = integrations?.find(
      (integration) => integration.integrationMetadata.crm
    );
    const { integrationType } = activeCRMIntegration || {};

    const { integrationId, disabled } = useIntegration({
      integrationType,
      organizationId,
    });

    const initialValues = useMemo(
      () =>
        demo &&
        user &&
        getInitialShareDemoModalValues(
          demo?.demoName,
          user,
          demo?.variables || [],
          flags.welcomeMatTitle
            ? undefined
            : `${organization.organizationName} product demo`
        ),
      [demo, user, flags?.welcomeMatTitle]
    );

    const [showShareDemoModal, { setFalse: setFalseShowShareDemoModal }] =
      useBoolean(true);
    const [
      showCopyShareLinkModal,
      {
        setTrue: setTrueCopyShareLinkModal,
        setFalse: setFalseCopyShareLinkModal,
      },
    ] = useBoolean(false);
    const [shareLink, setShareLink] = useState<DemoShare | null>(null);
    const [createDemoShare, { isLoading: isCreateDemoShareLoading }] =
      useCreateDemoShareMutation();
    const { trackEvent } = useAnalytics();
    const handleCreateShareLink = ({
      linkName,
      skipWelcome,
      cover,
      expiresAt,
      daysToExpire,
      security,
      client,
      variables,
      salesforceLeads,
      hubspotLeads,
      slackIntegration,
      opportunity,
      showGuidanceTooltip,
      welcomeMatTitle,
    }: ShareDemoModalState) => {
      const integrationMetadata =
        !disabled && opportunity?.id
          ? { integrationId, opportunityId: opportunity.id }
          : undefined;
      createDemoShare({
        workspaceId,
        organizationId: String(organizationId),
        demoId: String(demoId),
        forClient: client,
        variables,
        linkDescription: linkName,
        skipWelcome,
        cover: flags?.welcomeMatConfig ? cover : undefined,
        expiresAt,
        daysToExpire,
        security,
        hidden: true,
        integrationMetadata,
        showGuidanceTooltip,
        integrationLeads: Object.keys(
          removeUndefinedValues({
            salesforce: salesforceLeads,
            hubspot: hubspotLeads,
            slack: slackIntegration,
          })
        ) as IntegrationVendor[],
        welcomeMatTitle,
      })
        .unwrap()
        .then((share) => {
          notify.success('Link was created successfully');
          setTrueCopyShareLinkModal();
          setShareLink(share);
          onCreateSuccess?.(share);
        })
        .catch(() => {
          modal.remove();
          notify.error('Sorry, something went wrong');
        })
        .finally(setFalseShowShareDemoModal);
    };
    const location = 'demo_info_page_create_share_demo_modal';

    return (
      <>
        {initialValues && showShareDemoModal && getIsDemoShareable(demo) && (
          <ShareDemoModal
            demoId={String(demoId)}
            isSharedFromDemo
            isLoading={isCreateDemoShareLoading}
            title="Share demo"
            initialValues={initialValues}
            handleSubmit={(values) => {
              trackEvent(
                AnalyticsTrackEventName.DEMO_CLICK_SHARE_DEMO_APPROVE,
                {
                  location,
                  shareData: values,
                }
              );
              handleCreateShareLink(values);
            }}
            open={showShareDemoModal}
            onClose={() => {
              trackEvent(AnalyticsTrackEventName.DEMO_CLICK_SHARE_DEMO_CANCEL, {
                location,
              });
              modal.remove();
              setFalseShowShareDemoModal();
            }}
          />
        )}
        {shareLink && (
          <CopyShareLinkModal
            open={showCopyShareLinkModal}
            onClose={() => {
              modal.remove();
              setFalseCopyShareLinkModal();
            }}
            shareLink={shareLink as ShareLink}
            goToShares={goToShares}
            containerClass="w-[680px]"
          />
        )}
      </>
    );
  }
);
